import cx from 'classnames';
import {FC, useContext, useMemo} from 'react';
import {FabricTemplate} from '~/common/fabric/types';
import {useBrowserHeight} from '~/common/hooks/useBrowserHeight';
import {getResponsiveSizeToHintScroll} from '~/common/hooks/useResponsivelySizeToHintScroll';
import {OptionsGrid} from '~/QuickEcards/components/OptionsGrid/OptionsGrid';
import {BottomSheetTabContext} from '~/QuickEcards/context/BottomSheetTabContext';
import {OverlayIcon, TabNames} from '~/QuickEcards/types/misc';
import {Typography} from '~library/atoms/Typography';
import './FloatingOptionsPanel.sass';

interface FloatingOptionsPanelProps {
  assets: OverlayIcon[] | FabricTemplate[];
  onAssetClick(id: string): void;
}

export const FloatingOptionsPanel: FC<FloatingOptionsPanelProps> = ({assets, onAssetClick}) => {
  const {tab} = useContext(BottomSheetTabContext);
  const browserHeight = useBrowserHeight({timeout: 0});

  const maxCellHeight = useMemo(
    () =>
      getResponsiveSizeToHintScroll({
        numVisible: 4,
        containerSize: '520px', // Max height
        itemGap: 1,
        itemGapUnits: 'rem',
      }),
    []
  );

  const dynamicCellHeight = useMemo(() => {
    if (tab !== 'design') return undefined;
    const responsiveHeight = getResponsiveSizeToHintScroll({
      numVisible: browserHeight >= 750 ? 4 : 3,
      // Same height that's set on floating-options-panel by default
      containerSize: `(90vh - 9.375rem - 7rem)`,
      itemGap: 1,
      itemGapUnits: 'rem',
    });
    return `min(${responsiveHeight}, ${maxCellHeight})`;
  }, [browserHeight, tab, maxCellHeight]);

  if (!tab) return null;
  return (
    <div className={cx('floating-options-panel', tab)}>
      <Typography variant="label2" color="charcoal">
        {TabNames[tab]}
      </Typography>
      <div className="floating-options-panel__options-grid-container">
        <OptionsGrid
          className="floating-options-panel__options-grid"
          assets={assets}
          onAssetClick={onAssetClick}
          tab={tab}
          cellStyle={{height: dynamicCellHeight}}
        />
      </div>
    </div>
  );
};
